// FNP-1192 — VALUE VALIDATION, PER RECORD TYPE.
//
// ⚠️ WHY THIS IS CLIENT-SIDE AT ALL.
//
// The provider will reject a malformed value. But it rejects it AFTER a network
// round trip, as a VALIDATION_REJECTED with the provider's own wording, by
// which point the operator has left the form and is reading a toast. Catching
// it in the form means the message appears next to the field that is wrong,
// before anything is sent — which is the difference between a typo and an
// incident.
//
// ⚠️ THIS IS A GUARD, NOT A SPECIFICATION. The provider remains the authority:
// anything here that is stricter than a provider would reject a value the
// provider would have accepted, which is worse than letting it through. So each
// check below is deliberately the LOOSE one — structural shape only, never a
// judgement about whether the target exists or is reachable.

export type RecordValueProblem = {
    // Which value in the list failed, so the form can point at the right row.
    index: number,
    message: string,
}

const IPV4 = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;

// Structural only: hex groups and colons, with `::` compression allowed. Not a
// full RFC 4291 parser — that would reject valid addresses this module has no
// business rejecting.
const IPV6 = /^[0-9A-Fa-f:]+$/;

// A hostname as DNS accepts it: labels of letters, digits and hyphens, joined
// by dots, optionally fully qualified with a trailing dot. Underscores are
// allowed because `_dmarc` and `_acme-challenge` are real, common record names.
const HOSTNAME = /^(\*\.)?([A-Za-z0-9_-]+\.)*[A-Za-z0-9_-]+\.?$/;

const isIpv4 = (value: string): boolean => {
    const match = IPV4.exec(value);
    if (!match) {
        return false;
    }
    // Each octet in range. A regex alone would accept 999.999.999.999, which
    // every provider rejects and which is a plausible typo.
    return [match[1], match[2], match[3], match[4]].every(part => {
        const octet = Number(part);
        return Number.isInteger(octet) && octet >= 0 && octet <= 255;
    });
};

const isIpv6 = (value: string): boolean =>
    IPV6.test(value) && value.includes(':');

// ⚠️ AN IPv4 ADDRESS MATCHES THE HOSTNAME GRAMMAR, and rejecting it is the
// point of this function rather than an extra.
//
// Digits and dots are legal in DNS labels, so `203.0.113.10` satisfies the
// hostname pattern above — which would let an IP address through into a CNAME
// or an NS record, the single most common mistake in this form. A real
// hostname's rightmost label cannot be all digits (a TLD is alphabetic), and
// that is the cheap structural test which separates the two.
const isHostname = (value: string): boolean => {

    if (!HOSTNAME.test(value) || value.length > 253) {
        return false;
    }

    if (isIpv4(value)) {
        return false;
    }

    // The rightmost label, ignoring a trailing dot on a fully-qualified name.
    const labels = value.replace(/\.$/, '')
                        .split('.');
    const tld = labels[labels.length - 1];

    // A single bare label (`localhost`, or a relative record name some
    // providers accept) is allowed; only a DOTTED name is required to end in an
    // alphabetic label, which is what makes `10.0.0.1` fail and
    // `mail.example.com` pass.
    if (labels.length === 1) {
        return true;
    }

    return /[A-Za-z]/.test(tld);
};

/**
 * Check one value against the chosen record type.
 *
 * Returns a message when the value is structurally wrong for the type, or
 * undefined when it is acceptable — including for every type this does not
 * know, which is deliberate. An unrecognised type falls through to "any
 * non-empty string", because refusing a value for a type whose grammar this
 * module does not model would block a legitimate record.
 */
export const validateRecordValue = (type: string, value: string): string | undefined => {

    const trimmed = value.trim();

    if (trimmed === '') {
        return 'A value is required.';
    }

    switch (type.toUpperCase()) {

        case 'A':
            return isIpv4(trimmed)
                ? undefined
                : 'An A record takes an IPv4 address, for example 203.0.113.10.';

        case 'AAAA':
            return isIpv6(trimmed)
                ? undefined
                : 'An AAAA record takes an IPv6 address, for example 2001:db8::1.';

        // ⚠️ CNAME AND NS BOTH TAKE A HOSTNAME, and the copy differs because
        // the mistakes differ: people put an IP in a CNAME, and they put a bare
        // label in an NS.
        case 'CNAME':
            return isHostname(trimmed)
                ? undefined
                : 'A CNAME record takes a hostname, not an IP address — for example app.example.com.';

        case 'NS':
            return isHostname(trimmed)
                ? undefined
                : 'An NS record takes a nameserver hostname, for example ns-1.example.com.';

        case 'PTR':
            return isHostname(trimmed)
                ? undefined
                : 'A PTR record takes a hostname, for example host.example.com.';

        // `<priority> <hostname>` — the priority is what people leave out.
        case 'MX': {
            const parts = trimmed.split(/\s+/);
            if (parts.length !== 2) {
                return 'An MX record takes a priority and a hostname, for example 10 mail.example.com.';
            }
            const priority = Number(parts[0]);
            if (!Number.isInteger(priority) || priority < 0 || priority > 65535) {
                return 'The MX priority must be a whole number between 0 and 65535, for example 10 mail.example.com.';
            }
            return isHostname(parts[1])
                ? undefined
                : 'The MX target must be a hostname, for example 10 mail.example.com.';
        }

        // `<priority> <weight> <port> <target>`.
        case 'SRV': {
            const parts = trimmed.split(/\s+/);
            if (parts.length !== 4) {
                return 'An SRV record takes priority, weight, port and target, for example 10 5 443 sip.example.com.';
            }
            const numbers = parts.slice(0, 3)
                                 .map(Number);
            if (numbers.some(n => !Number.isInteger(n) || n < 0 || n > 65535)) {
                return 'The SRV priority, weight and port must be whole numbers between 0 and 65535.';
            }
            return isHostname(parts[3])
                ? undefined
                : 'The SRV target must be a hostname, for example 10 5 443 sip.example.com.';
        }

        // `<flags> <tag> <value>`.
        case 'CAA': {
            const parts = trimmed.split(/\s+/);
            if (parts.length < 3) {
                return 'A CAA record takes flags, a tag and a value, for example 0 issue "letsencrypt.org".';
            }
            const flags = Number(parts[0]);
            if (!Number.isInteger(flags) || flags < 0 || flags > 255) {
                return 'The CAA flags must be a whole number between 0 and 255.';
            }
            return undefined;
        }

        // ⚠️ TXT IS DELIBERATELY UNVALIDATED BEYOND NON-EMPTY. A TXT value is
        // arbitrary text — SPF, DKIM keys, domain verification tokens — and
        // every structural guess about it would be wrong for some real record.
        case 'TXT':
            return undefined;

        // Any type this module does not model. Non-empty is the only claim it
        // can honestly make.
        default:
            return undefined;
    }
};

/**
 * Check every value in the list.
 *
 * Returns one problem per bad value, indexed, so the form can mark the exact
 * row rather than reporting "something is wrong" over the whole field.
 */
export const validateRecordValues = (
    type: string,
    values: string[],
): RecordValueProblem[] =>
    values
        .map((value, index) => {
            const message = validateRecordValue(type, value);
            return message ? {index, message} : undefined;
        })
        .filter((problem): problem is RecordValueProblem => Boolean(problem));

// TTL bounds. 0 is legal at some providers and means "do not cache"; the upper
// bound is the one-week value every provider accepts. Anything outside is a
// typo far more often than an intent.
export const MIN_TTL = 0;
export const MAX_TTL = 604800;

export const validateTtl = (ttlSeconds: number | undefined): string | undefined => {
    if (typeof ttlSeconds !== 'number' || Number.isNaN(ttlSeconds)) {
        return 'A time to live is required.';
    }
    if (!Number.isInteger(ttlSeconds)) {
        return 'The time to live must be a whole number of seconds.';
    }
    if (ttlSeconds < MIN_TTL || ttlSeconds > MAX_TTL) {
        return `The time to live must be between ${MIN_TTL} and ${MAX_TTL} seconds.`;
    }
    return undefined;
};

// A record name. Empty is not allowed — the zone apex is written as the zone's
// own name, not as a blank.
export const validateRecordName = (name: string): string | undefined => {
    const trimmed = name.trim();
    if (trimmed === '') {
        return 'A record name is required.';
    }
    if (!isHostname(trimmed)) {
        return 'A record name must be a hostname, for example www.example.com.';
    }
    return undefined;
};
